import { createSlice } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

const reduxInstance = MojitoCore.Services.redux;
const { actions: betslipActions } = MojitoServices.Betslip;
const { types: BetsTypes } = MojitoServices.Bets;

/**
 * The name of the match acca modules store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Modules.MatchAcca
 */
export const STORE_KEY = 'modules/matchAcca';

/**
 * Defines the state of match acca view store.
 *
 * @typedef MatchAccaViewState
 *
 * @property {boolean} isBuilding - Indicates the match acca builder state.
 * @property {string} eventId - The id of the event for which the match acca is being built.
 * @property {boolean} isAddingToBetslip - Indicates whether match acca is being added to betslip.
 *
 * @memberof Mojito.Modules.MatchAcca
 */
export const initialState = {
    isBuilding: false,
    eventId: undefined,
    isAddingToBetslip: false,
};

export const { reducer, actions } = createSlice({
    name: STORE_KEY,
    initialState,
    reducers: {
        startBuilding(state, { payload: eventId }) {
            state.isBuilding = true;
            state.eventId = eventId;
        },
        stopBuilding(state) {
            state.isBuilding = false;
            state.eventId = undefined;
        },
        reset() {
            return { ...initialState };
        },
    },
    extraReducers: builder => {
        builder
            .addCase(betslipActions.addCompositePending, (state, { payload: { legSort } }) => {
                if (legSort === BetsTypes.LEG_SORT.MATCH_ACCA) {
                    state.isAddingToBetslip = true;
                }
            })
            .addCase(betslipActions.addCompositeSuccess, (state, { payload }) => {
                if (payload?.requestPayload.legSort === BetsTypes.LEG_SORT.MATCH_ACCA) {
                    state.isAddingToBetslip = false;
                }
            });
    },
});

/**
 * Actions related to the MatchAcca module.
 *
 * @class MatchAccaActions
 * @name actions
 * @memberof Mojito.Modules.MatchAcca
 */

/**
 * Start building.
 *
 * @function startBuilding
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {string} eventId - Id of the event building has been started for.
 *
 * @memberof Mojito.Modules.MatchAcca.actions
 */

/**
 * Stop building.
 *
 * @function stopBuilding
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Modules.MatchAcca.actions
 */

/**
 * Reset MatchAcca state.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Modules.MatchAcca.actions
 */

reduxInstance.injectReducer(STORE_KEY, reducer);
